import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import Button from "@/components/common/Button";
import TextEditor from "@/components/common/inputs/Text_Editor";
import ImagePicker from "@/components/specific/Image_Picker";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import { CloseOutlined, SmileOutlined, PictureOutlined } from "@/config/antd";

const MessageDialog = ({ isVisible, onClose, user }) => {
  const [message, setMessage] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    setIsActive(message.trim() !== "");
  }, [message]);

  const handleAddImage = (file) => {
    setSelectedImages([...selectedImages, file]);
  };

  const handleDeleteImage = (index) => {
    setSelectedImages(selectedImages.filter((_, i) => i !== index));
  };

  const handleSendMessage = () => {
    onClose();
    showLoader();

    // Simulate API call
    setTimeout(() => {
      hideLoader();
      setIsSuccessVisible(true);
    }, 2000);
  };

  const handleCloseSuccess = () => {
    setIsSuccessVisible(false);
    onClose();
    setMessage("");
    setSelectedImages([]);
    setShowImagePicker(false);
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={350}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}
        className="custom-message-modal">
        <div className="flex flex-col bg-white">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center">
              <Avatar
                src={user.avatar}
                size={40}
                shape="circle"
                className="mr-2"
              />
              <span className="text-bold-x text-primary">
                Message {user.name}
              </span>
            </div>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-secondary)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          <div className="w-full border-t border-primary"></div>

          <div className="p-4">
            <TextEditor
              label="DESCRIPTION"
              placeholder="Enter Texts"
              value={message}
              onChange={setMessage}
            />

            <div className="flex items-center mt-2">
              <SmileOutlined className="text-lg mr-2 cursor-pointer" />
              <PictureOutlined
                className="text-lg cursor-pointer"
                onClick={() => setShowImagePicker(!showImagePicker)}
              />
            </div>

            {showImagePicker && (
              <div className="mt-4">
                <ImagePicker
                  images={selectedImages}
                  onAddImage={handleAddImage}
                  onDeleteImage={handleDeleteImage}
                />
              </div>
            )}
          </div>

          <div className="w-full bg-secondary p-4 flex gap-3">
            <Button
              text="Cancel"
              color="default"
              className="flex-1"
              customBgColor="white"
              customTextColor="darkGray"
              onClick={onClose}
            />
            <Button
              text="Send"
              onClick={handleSendMessage}
              isActive={isActive}
              color="darkBlue"
              className={`flex-1 ${
                isActive ? "bg-primary text-white" : "bg-darkBlue text-primary"
              }`}
            />
          </div>
        </div>
      </Modal>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleCloseSuccess}
        header="Message Sent"
        paragraph={`Your message has been successfully sent to <span class="text-primary text-bold">${user.name}</span>.`}
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={handleCloseSuccess}
      />
    </>
  );
};

MessageDialog.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  user: PropTypes.shape({
    avatar: PropTypes.string,
    name: PropTypes.string,
  }),
};

export default MessageDialog;
